import type { Recipe, Unit } from '@/service/recipe/types';
import { scaleIngredients, servingsOf } from '@/service/recipe/scaling';
import {
  type Day,
  SLOTS,
  type Slot,
  type WeekPlan,
} from '@/service/weekplan/types';

// One line of the week's shopping list: an ingredient summed over the meals
// that need it
export interface ShoppingLine {
  key: string;
  text: string;
  amount: number;
  unit: Unit;
  recipeId: string;
  // Titles of the recipes it is for, in plan order
  meals: string[];
}

// A planned meal without a recipe (or whose recipe is gone): its groceries
// have to be added by hand
export interface UnlistedMeal {
  day: Day;
  slot: Slot;
  title: string;
}

export interface ShoppingList {
  lines: ShoppingLine[];
  unlisted: UnlistedMeal[];
}

function lineKey(text: string, unit: Unit): string {
  return `${text.trim().toLowerCase().replace(/\s+/g, ' ')}|${unit}`;
}

// Every ingredient of the planned recipes, scaled to each meal's servings,
// in the order of the week (days from the week start, breakfast to dinner)
export function collectShoppingList(
  plan: WeekPlan,
  recipes: Recipe[],
  days: readonly Day[],
): ShoppingList {
  const lines = new Map<string, ShoppingLine>();
  const unlisted: UnlistedMeal[] = [];

  for (const day of days) {
    for (const slot of SLOTS) {
      const meal = plan.meals[day]?.[slot];
      if (!meal) continue;
      const recipe = recipes.find(({ id }) => id === meal.recipeId);
      if (!recipe) {
        unlisted.push({ day, slot, title: meal.title });
        continue;
      }
      const from = servingsOf(recipe);
      const ingredients = scaleIngredients(
        recipe.ingredients,
        from,
        meal.servings ?? from,
      );
      for (const { text, amount, unit } of ingredients) {
        if (!text.trim()) continue;
        const key = lineKey(text, unit);
        const by = amount > 0 ? amount : 0;
        const line = lines.get(key);
        if (line) {
          line.amount += by;
          if (!line.meals.includes(recipe.title)) {
            line.meals.push(recipe.title);
          }
        } else {
          lines.set(key, {
            key,
            text: text.trim(),
            amount: by,
            unit,
            recipeId: recipe.id,
            meals: [recipe.title],
          });
        }
      }
    }
  }

  return { lines: [...lines.values()], unlisted };
}
